import { ConstantValues } from "common/constants";
import { IColumnActionsMenuProps } from "components/columnActionsMenu/ColumnActionsMenu.types";
import { SortingDirection } from "models/enums/SortingOrder";
import { ITableColumn } from "models/ITableColumn";
import { useCallback, useEffect, useState } from "react";

interface IUseAdminListSortConfig {
  cacheKey: string;
  defaultSortColumn?: string;
  defaultDescending?: boolean;
}

interface IUseAdminListSortReturn<T> {
  sortColumn: string | null;
  isSortDescending: boolean;
  getColumnActions: (column: ITableColumn<T>) => IColumnActionsMenuProps;
  sortItems: (items: T[], columns: ITableColumn<T>[]) => T[];
  onSortColumnChanged: (columnKey: string, descending: boolean) => void;
  onClearColumnChanged: () => void;
}

function parseSortingOptions(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(ConstantValues.SORTING_OPTIONS_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function useAdminListSort<T>(
  config: IUseAdminListSortConfig,
): IUseAdminListSortReturn<T> {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [isSortDescending, setIsSortDescending] = useState<boolean>(false);

  useEffect(() => {
    const parsedOptions = parseSortingOptions();
    const listSortingOptions = parsedOptions[config.cacheKey] as
      | { column: string; direction: string }
      | undefined;

    if (listSortingOptions) {
      setSortColumn(listSortingOptions.column);
      setIsSortDescending(listSortingOptions.direction === "desc");
      return;
    }

    if (config.defaultSortColumn) {
      setSortColumn(config.defaultSortColumn);
      setIsSortDescending(config.defaultDescending ?? false);
    }
  }, [config.cacheKey, config.defaultSortColumn, config.defaultDescending]);

  const onSortColumnChanged = (
    columnKey: string,
    descending: boolean,
  ): void => {
    setSortColumn(columnKey);
    setIsSortDescending(descending);

    const sortingOptions = parseSortingOptions();
    sortingOptions[config.cacheKey] = {
      column: columnKey,
      direction: descending ? "desc" : "asc",
    };

    localStorage.setItem(
      ConstantValues.SORTING_OPTIONS_CACHE_KEY,
      JSON.stringify(sortingOptions),
    );
  };

  const onClearColumnChanged = (): void => {
    setSortColumn(null);
    setIsSortDescending(false);

    const sortingOptions = parseSortingOptions();
    delete sortingOptions[config.cacheKey];

    localStorage.setItem(
      ConstantValues.SORTING_OPTIONS_CACHE_KEY,
      JSON.stringify(sortingOptions),
    );
  };

  const getColumnActions = (
    column: ITableColumn<T>,
  ): IColumnActionsMenuProps => {
    const isColumnSorted: boolean = sortColumn === column.columnId;
    const isColumnSortable: boolean =
      column.isSortable === undefined ? true : column.isSortable;

    return {
      columnKey: column.columnId.toString(),
      displayValue: column.displayValue,
      isSortable: isColumnSortable,
      sortingDirection: isColumnSorted
        ? isSortDescending
          ? SortingDirection.Descending
          : SortingDirection.Ascending
        : SortingDirection.None,
      onSortClicked: (columnKey: string, descending: boolean) =>
        onSortColumnChanged(columnKey, descending),
    };
  };

  const sortItems = useCallback(
    (items: T[], columns: ITableColumn<T>[]): T[] => {
      const sorted = [...items];

      if (sortColumn) {
        const column = columns.find((col) => col.columnId === sortColumn);
        if (column && column.compare) {
          sorted.sort(
            (a, b) => column.compare(a, b) * (isSortDescending ? -1 : 1),
          );
        }
      }

      return sorted;
    },
    [sortColumn, isSortDescending],
  );

  return {
    sortColumn,
    isSortDescending,
    getColumnActions,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  };
}
